import { Model, Request } from '@viddsee/server-api-client';
import { identifiers as userPlaylistIdentifierDefinitions } from '@/scripts/models/videoCollections/identifierDefinitions/userPlaylist';
import { playlistsApi } from '@/scripts/webcall/v3/playlists';
import { usersApi } from '@/scripts/webcall/v3/users';
import { CollectionImplementation, CollectionImplementationData, CollectionImplementationOptions } from './core/BaseCollectionImplementation';
import { HarmonyPromise, Nullable } from '../../utility-types';

export class PlaylistCollectionImplementation extends CollectionImplementation {
  constructor({ video, params }: CollectionImplementationOptions) {
    super({
      video,
      params,
      identifierDefinitions: userPlaylistIdentifierDefinitions,
    });
  }

  test(): boolean {
    return !!this.params.playlist;
  }

  async provide(): Promise<Nullable<CollectionImplementationData>> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const implementation = this;

    const { playlist: playlistSlug } = this.params as { playlist: string };

    let playlistInfo: Model.Playlist.VideoCollectionInfo | null = null;
    try {
      const { data } = await playlistsApi.show({
        playlistSlug,
      });
      playlistInfo = data;
    } catch (error) {
      return null;
    }

    const playlistAuthor = playlistInfo.user;

    return new CollectionImplementationData({
      implementation,
      publicUrl: `/u/${playlistAuthor.vanity}/${this.identifierKVMap.playlist.ids.publicId}/${playlistInfo.slug}`,
      author: {
        publicUrl: `/u/${playlistAuthor.vanity}`,
        displayName: `${playlistAuthor.name}`,
      },
      fetchers: {
        info(): HarmonyPromise<Nullable<Model.VideoCollection.Info>> {
          return Promise.resolve(playlistInfo as Model.Playlist.VideoCollectionInfo);
        },

        async videoIndex(): Promise<number> {
          if (!implementation.video) return -1;
          try {
            const { data: videoIndex } = await usersApi.playlists.findVideoIndex({
              playlistSlug,
              userVanity: playlistAuthor.vanity,
            }, {
              videoId: implementation.video.id,
            });
            return videoIndex.index;
          } catch (error) {
            return -1;
          }
        },

        async videos(params = {}): Promise<Model.Pagination.PaginatedItemsResponse<Model.Video.Info>> {
          const defaultPagination = {
            order: params.order != null ? params.order : Request.Pagination.OptionOrder.Ascending,
            page: params.page != null ? params.page : 0,
            perPage: params.perPage != null ? params.perPage : 12,
          };

          try {
            const { data: paginatedVideoInfoResponse } = await usersApi.playlists.listVideos({
              playlistSlug,
              userVanity: playlistAuthor.vanity,
            }, {
              page: defaultPagination.page,
              order: defaultPagination.order,
              perPage: defaultPagination.perPage,
            });
            return paginatedVideoInfoResponse;
          } catch (error) {
            // TODO: Log this to sentry
            // eslint-disable-next-line no-console
            console.error(error);
            return {
              ...defaultPagination,
              pageCount: 0,
              itemCount: 0,
              items: [],
            };
          }
        },
      },
    });
  }
}
